import React from 'react';
import { X, Clock, Layers, Package, Zap, Download, FileText } from 'lucide-react';
import type { Procedure } from '../data/constants.js';
import { C } from '../data/constants.js';
import { ActionBtn } from './ActionBtn.js';

interface ProcedureDetailProps {
  proc: Procedure;
  onClose: () => void;
}

export function ProcedureDetail({ proc, onClose }: ProcedureDetailProps): React.ReactElement {
  const stats = [
    { label: 'Duration', value: `${proc.duration}m`, icon: Clock },
    { label: 'Steps', value: `${proc.stepsCompleted}/${proc.steps}`, icon: Layers },
    ...(proc.wasteCost != null
      ? [{ label: 'Waste', value: `$${proc.wasteCost}`, icon: Package }]
      : []),
    ...(proc.deadTime != null
      ? [{ label: 'Dead Time Saved', value: `-${proc.deadTime}m`, icon: Zap }]
      : []),
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 60,
        overflowY: 'auto',
        animation: 'insights-fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 700,
          background: '#0D0F12',
          border: `1px solid ${C.cardBorder}`,
          borderRadius: 20,
          padding: 32,
          marginBottom: 60,
          animation: 'insights-fadeSlideUp 0.4s ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 24,
          }}
        >
          <div>
            <h3 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>{proc.name}</h3>
            <p style={{ fontSize: 13, color: C.textDim, margin: '6px 0 0' }}>
              {proc.surgeon} &middot; {proc.date} &middot; {proc.or || 'No room'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: `1px solid ${C.cardBorder}`,
              background: C.card,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} color={C.textMid} />
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
            gap: 12,
            marginBottom: 28,
          }}
        >
          {stats.map(({ label, value, icon: I }) => (
            <div
              key={label}
              style={{
                background: 'rgba(255,255,255,0.03)',
                borderRadius: 12,
                padding: '14px 16px',
                border: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <I size={14} color={C.accent} />
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#fff',
                  marginTop: 8,
                  fontFamily: "'Space Mono', monospace",
                }}
              >
                {value}
              </div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {proc.steps > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: C.textMid, margin: '0 0 12px' }}>
              Timestamps / Steps
            </h4>
            <div
              style={{
                background: C.card,
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.05)',
                overflow: 'hidden',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {Array.from({ length: proc.steps }, (_, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: i < proc.stepsCompleted ? C.accent : `${C.red}99`,
                      }}
                    />
                    <span style={{ fontSize: 12, color: C.textMid }}>Step {i + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {proc.wasteItems && proc.wasteItems.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: C.textMid, margin: '0 0 12px' }}>
              Waste Items
            </h4>
            <div
              style={{
                background: C.card,
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.05)',
                overflow: 'hidden',
              }}
            >
              {proc.wasteItems.map((w, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                  }}
                >
                  <span style={{ fontSize: 12, color: C.textMid }}>{w.name}</span>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <span
                      style={{
                        fontSize: 12,
                        color: C.textDim,
                        fontFamily: "'Space Mono', monospace",
                      }}
                    >
                      x{w.count}
                    </span>
                    {w.cost > 0 && (
                      <span
                        style={{
                          fontSize: 12,
                          color: C.orange,
                          fontFamily: "'Space Mono', monospace",
                        }}
                      >
                        ${w.cost}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <ActionBtn icon={Download} label="Export Timestamps (CSV)" accent />
          <ActionBtn icon={FileText} label="Export Report (PDF)" />
        </div>
      </div>
    </div>
  );
}
